import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  FINDING_CLASSIFICATION_LABELS,
  type InternalAudit,
  type InternalAuditFinding,
  type RaiseCorrectiveActionInput,
} from '@grc/types';
import { apiFetch } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import { Button, Dialog, Field, Input, Select, useToast } from '../../components/ui.js';
import { auditKey, errorMessage, issuesOf, itemsKey, memberOptions, useMembers } from './shared.js';

const PRIORITY_LABEL: Record<string, string> = {
  major_nonconformity: 'High',
  minor_nonconformity: 'Medium',
  observation: 'Low',
  opportunity_for_improvement: 'Low',
};

/**
 * Raise a corrective-actions record from a finding (procedure §7): owner and
 * target date; the rest is composed server-side from the finding.
 */
export function RaiseDialog({
  audit,
  finding,
  open,
  onClose,
}: {
  audit: InternalAudit;
  finding: InternalAuditFinding | null;
  open: boolean;
  onClose: () => void;
}) {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const members = useMembers(org.orgId, open);
  const [owner, setOwner] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [defaulted, setDefaulted] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOwner('');
    setDueDate('');
    setErrors({});
    setDefaulted(false);
  }, [open, finding?.id]);

  // The server falls back to the lead auditor whenever no owner is sent, for
  // any member of the organisation — disabled or not (procedure §7). Mirror
  // that rule here, so what the dialog shows is what the register records.
  const leadIsMember =
    !!audit.leadAuditorId && (members.data ?? []).some((m) => m.user.id === audit.leadAuditorId);

  // Default owner once the member list is known. Applied once per opening so
  // a choice is never overwritten.
  useEffect(() => {
    if (!open || defaulted || !members.data) return;
    const lead = audit.leadAuditorId;
    if (lead && members.data.some((m) => m.user.id === lead)) setOwner(lead);
    setDefaulted(true);
  }, [open, defaulted, members.data, audit.leadAuditorId]);

  const raise = useMutation({
    mutationFn: () => {
      const body: RaiseCorrectiveActionInput = {
        ownerUserId: owner || null,
        ...(dueDate ? { dueDate } : {}),
      };
      return apiFetch<InternalAuditFinding>(`/internal-audits/${audit.id}/findings/${finding!.id}/raise`, {
        method: 'POST',
        body,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: itemsKey(audit.id) });
      void queryClient.invalidateQueries({ queryKey: auditKey(audit.id) });
      // The corrective-actions register now has a new record.
      void queryClient.invalidateQueries({ queryKey: ['records', org.orgId] });
      toast('Corrective action raised in the register.', 'success');
      onClose();
    },
    onError: (err) => {
      setErrors(issuesOf(err));
      toast(errorMessage(err, 'Could not raise the corrective action'), 'error');
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!finding || raise.isPending) return;
    raise.mutate();
  };

  // A disabled lead auditor is still a member the server would assign, so the
  // select must be able to show them.
  const ownerOptions = memberOptions(
    members.data,
    leadIsMember ? audit.leadAuditorId : null,
    audit.leadAuditorName,
  );

  return (
    <Dialog open={open} onClose={onClose} title="Raise corrective action">
      {finding && (
        <form onSubmit={submit}>
          <p>
            A record is created in the Corrective Actions register from this finding, with source
            “Internal audit”, reference “{audit.name} — {finding.clauseNumber}” and priority{' '}
            <strong>{PRIORITY_LABEL[finding.classification] ?? 'Low'}</strong> (
            {FINDING_CLASSIFICATION_LABELS[finding.classification].toLowerCase()}). The finding is
            marked as actioned.
          </p>
          <Field
            label="Owner"
            hint={
              leadIsMember
                ? 'Who is responsible for the corrective action — left unset, the lead auditor is recorded as the owner'
                : 'Who is responsible for the corrective action'
            }
            error={errors.ownerUserId}
          >
            {(props) => (
              <Select {...props} value={owner} onChange={(e) => setOwner(e.target.value)}>
                <option value="">
                  {leadIsMember
                    ? `Lead auditor (${audit.leadAuditorName ?? 'unnamed'}) — default`
                    : 'Not assigned'}
                </option>
                {ownerOptions.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Target date" hint="Optional" error={errors.dueDate}>
            {(props) => (
              <Input {...props} type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
            )}
          </Field>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={onClose} disabled={raise.isPending}>
              Cancel
            </Button>
            <Button type="submit" loading={raise.isPending}>
              Raise corrective action
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
